import { BaseApiService } from '../base/BaseApiService';
import { ApiResponse } from '../../types/api';

export interface FilasVtmeFetchOptions {
  signal?: AbortSignal;
}

export interface FilasVtmeStageStatusMoveDto {
  statusVtme: string;
  entries: number;
  exits: number;
}

export interface FilasVtmeStageQueueStatusDto {
  statusVtme: string;
  slaMinutes: number;
  slaEntries: number;
}

export interface FilasVtmeStageQueueInfoDto {
  id: string;
  name: string;
  sortOrder: number;
  slaMinutes?: number;
  slaEntries?: number;
  slaMoves?: number;
  statuses?: FilasVtmeStageQueueStatusDto[];
}

export interface FilasVtmeStageSlaItemDto {
  pedidoId: number;
  contractId?: string | null;
  clientName?: string | null;
  cnpj?: string | null;
  queueId: string;
  queueName: string;
  stage: string;
  statusVtme: string;
  enteredAtUtc: string;
  elapsedMinutes: number;
  slaMinutes: number;
  statusEntries?: number;
  slaEntries?: number;
  withinSlaMinutes?: boolean;
  withinSlaEntries?: boolean;
  queueEnteredAtUtc?: string;
  queueElapsedMinutes?: number;
  queueSlaMinutes?: number;
  queueEntries?: number;
  queueSlaEntries?: number;
  withinQueueSlaMinutes?: boolean;
  withinQueueSlaEntries?: boolean;
  queueMoves?: number;
  queueSlaMoves?: number;
  withinQueueSlaMoves?: boolean;
  withinSla: boolean;
  clockSource?: string | null;
  queueStatusMoves?: FilasVtmeStageStatusMoveDto[];
  queueStatusEntries?: number;
  insercaoUtc?: string | null;
  ultimaTrocaStatusUtc?: string | null;
  queueExitedAtUtc?: string | null;
  passageOpen?: boolean;
}

export interface FilasVtmeRecortePreferenceDto {
  found: boolean;
  mode: string;
  dateAnchor: string;
  periodPreset: string;
  fromDate: string;
  toDate: string;
  scope: string;
}

export interface FilasVtmeRecortePreferenceUpsertDto {
  mode: string;
  dateAnchor: string;
  periodPreset: string;
  fromDate: string;
  toDate: string;
  scope: string;
}

export interface FilasVtmeExportFiltersDto {
  mode: string;
  modeLabel: string;
  dateAnchor: string;
  dateAnchorLabel: string;
  period: string;
  scope: string;
  scopeLabel: string;
  summary: string;
  tableFiltersNote?: string | null;
}

export interface FilasVtmeExportStatusDto {
  statusVtme: string;
  total: number;
  ok: number;
  warn: number;
  bad: number;
  medianTempo?: string | null;
  medianEntradas?: string | null;
  showSlaTempo?: boolean;
  showSlaEntradas?: boolean;
}

export interface FilasVtmeExportQueueDto {
  queueId: string;
  queueName: string;
  sortOrder: number;
  total: number;
  ok: number;
  warn: number;
  bad: number;
  medianTempo?: string | null;
  medianEntradas?: string | null;
  medianMoves?: string | null;
  slaTempo?: string | null;
  slaEntradas?: string | null;
  slaMoves?: string | null;
  showSlaTempo?: boolean;
  showSlaEntradas?: boolean;
  showSlaMoves?: boolean;
  statuses: FilasVtmeExportStatusDto[];
}

export interface FilasVtmeExportPedidoDto {
  queueId: string;
  queueName: string;
  pedidoId: number;
  cliente: string;
  cnpj: string;
  statusVtme: string;
  dataInsercao: string;
  ultimaTrocaStatus: string;
  tempoNaFila?: string | null;
  movimentos?: string | null;
  entradas?: string | null;
  rowTone: 'ok' | 'warn' | 'bad';
  tempoTone: 'ok' | 'warn' | 'bad' | 'neutral';
  movimentosTone: 'ok' | 'warn' | 'bad' | 'neutral';
  entradasTone: 'ok' | 'warn' | 'bad' | 'neutral';
}

export interface FilasVtmeExportRequestDto {
  format: 'xlsx' | 'pdf';
  queueId?: string | null;
  /** Já formatado (dd/MM/yyyy HH:mm) — última carga da automação VTME. */
  ultimaAtualizacaoVtme?: string | null;
  filters: FilasVtmeExportFiltersDto;
  queues: FilasVtmeExportQueueDto[];
  pedidos: FilasVtmeExportPedidoDto[];
}

export interface FilasVtmeStageSlaResponseDto {
  available: boolean;
  unavailableReason?: string | null;
  syncedThroughNote?: string | null;
  /** ISO UTC da última carga success da automação de coleta VTME. */
  ultimaAtualizacaoUtc?: string | null;
  queues: FilasVtmeStageQueueInfoDto[];
  items: FilasVtmeStageSlaItemDto[];
  emilyReport?: unknown;
  tatiPending?: unknown;
}

export interface FilasVtmeStageQueueDto {
  id?: string;
  name: string;
  sortOrder: number;
  slaMinutes: number;
  slaEntries: number;
  slaMoves: number;
  statuses: FilasVtmeStageQueueStatusDto[];
}

export interface FilasVtmeStageQueueSettingsResponseDto {
  queues: FilasVtmeStageQueueDto[];
  isDefault: boolean;
}

export interface FilasVtmeVtmeStatusesResponseDto {
  status: string;
  count: number;
  valores: string[];
  /** cache | webhook | mixed */
  source?: string;
  addedCount?: number | null;
}

class FilasVtmeService extends BaseApiService {
  async listStageSla(
    opts?: FilasVtmeFetchOptions & {
      mode?: string;
      dateAnchor?: string;
      diasInseridos?: number | null;
      fromUtc?: string | null;
      toUtc?: string | null;
    }
  ): Promise<FilasVtmeStageSlaResponseDto> {
    const params: Record<string, string> = {};
    if (opts?.mode) params.mode = opts.mode;
    if (opts?.dateAnchor) params.dateAnchor = opts.dateAnchor;
    if (opts?.diasInseridos != null && opts.diasInseridos > 0) {
      params.diasInseridos = String(opts.diasInseridos);
    }
    if (opts?.fromUtc) params.fromUtc = opts.fromUtc;
    if (opts?.toUtc) params.toUtc = opts.toUtc;

    const response = await this.api.get<ApiResponse<FilasVtmeStageSlaResponseDto>>(
      '/vtme-filas/stage-sla',
      { signal: opts?.signal, params }
    );
    if (opts?.signal?.aborted) {
      throw opts.signal.reason ?? new DOMException('Aborted', 'AbortError');
    }
    return this.extractData(response);
  }

  async getStageQueueSettings(opts?: FilasVtmeFetchOptions): Promise<FilasVtmeStageQueueSettingsResponseDto> {
    const response = await this.api.get<ApiResponse<FilasVtmeStageQueueSettingsResponseDto>>(
      '/vtme-filas/queue-settings',
      { signal: opts?.signal }
    );
    if (opts?.signal?.aborted) {
      throw opts.signal.reason ?? new DOMException('Aborted', 'AbortError');
    }
    return this.extractData(response);
  }

  async saveStageQueueSettings(
    queues: FilasVtmeStageQueueDto[],
    opts?: FilasVtmeFetchOptions
  ): Promise<FilasVtmeStageQueueSettingsResponseDto> {
    const response = await this.api.put<ApiResponse<FilasVtmeStageQueueSettingsResponseDto>>(
      '/vtme-filas/queue-settings',
      { queues },
      { signal: opts?.signal }
    );
    if (opts?.signal?.aborted) {
      throw opts.signal.reason ?? new DOMException('Aborted', 'AbortError');
    }
    return this.extractData(response);
  }

  async getRecortePreference(opts?: FilasVtmeFetchOptions): Promise<FilasVtmeRecortePreferenceDto> {
    const response = await this.api.get<ApiResponse<FilasVtmeRecortePreferenceDto>>(
      '/vtme-filas/recorte-preference',
      { signal: opts?.signal }
    );
    if (opts?.signal?.aborted) {
      throw opts.signal.reason ?? new DOMException('Aborted', 'AbortError');
    }
    return this.extractData(response);
  }

  async saveRecortePreference(
    body: FilasVtmeRecortePreferenceUpsertDto,
    opts?: FilasVtmeFetchOptions
  ): Promise<FilasVtmeRecortePreferenceDto> {
    const response = await this.api.put<ApiResponse<FilasVtmeRecortePreferenceDto>>(
      '/vtme-filas/recorte-preference',
      body,
      { signal: opts?.signal }
    );
    if (opts?.signal?.aborted) {
      throw opts.signal.reason ?? new DOMException('Aborted', 'AbortError');
    }
    return this.extractData(response);
  }

  /** Catálogo local (rápido). */
  async listVtmeStatuses(opts?: FilasVtmeFetchOptions): Promise<string[]> {
    const response = await this.api.get<ApiResponse<FilasVtmeVtmeStatusesResponseDto>>(
      '/vtme-filas/vtme-statuses',
      { signal: opts?.signal }
    );
    if (opts?.signal?.aborted) {
      throw opts.signal.reason ?? new DOMException('Aborted', 'AbortError');
    }
    const data = this.extractData(response);
    return data.valores ?? [];
  }

  /** Sync silencioso com webhook VTME; upsert no catálogo local. */
  async syncVtmeStatuses(opts?: FilasVtmeFetchOptions): Promise<string[]> {
    const response = await this.api.post<ApiResponse<FilasVtmeVtmeStatusesResponseDto>>(
      '/vtme-filas/vtme-statuses/sync',
      {},
      { signal: opts?.signal, timeout: 120000 }
    );
    if (opts?.signal?.aborted) {
      throw opts.signal.reason ?? new DOMException('Aborted', 'AbortError');
    }
    const data = this.extractData(response);
    return data.valores ?? [];
  }

  async exportStageSla(body: FilasVtmeExportRequestDto): Promise<void> {
    try {
      const response = await this.api.post('/vtme-filas/stage-sla/export', body, {
        responseType: 'blob',
        timeout: 120000,
      });
      const format = (body.format || 'xlsx').toLowerCase();
      const fallback = format === 'pdf' ? 'filas-vtme-export.pdf' : 'filas-vtme-export.xlsx';
      const contentType =
        format === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      downloadBlob(
        response.data,
        fileNameFromDisposition(response.headers['content-disposition'], fallback),
        contentType
      );
    } catch (error: unknown) {
      throw new Error(await readBlobErrorMessage(error, 'Não foi possível exportar as Filas VTME.'));
    }
  }
}

function fileNameFromDisposition(header: unknown, fallback: string): string {
  const disposition = String(header || '');
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  return match ? decodeURIComponent(match[1]) : fallback;
}

function downloadBlob(data: BlobPart, fileName: string, contentType: string) {
  const blob = new Blob([data], { type: contentType });
  const href = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(href);
}

async function readBlobErrorMessage(error: unknown, fallback: string): Promise<string> {
  const axiosLike = error as {
    response?: { data?: unknown };
    message?: string;
  } | null;
  const data = axiosLike?.response?.data;
  if (data instanceof Blob) {
    const text = await data.text();
    try {
      const parsed = JSON.parse(text) as { message?: unknown };
      if (parsed?.message) return String(parsed.message);
    } catch {
      if (text.trim()) return text;
    }
  }
  if (data && typeof data === 'object' && 'message' in data && (data as { message?: unknown }).message) {
    return String((data as { message: unknown }).message);
  }
  return axiosLike?.message || fallback;
}

export const filasVtmeService = new FilasVtmeService();
export default filasVtmeService;
